<template>
  <div ref="timePicker" :id="pickerId" class="relative">
    <div type="button" class="d-flex w100" @click="showPicker">
      <div class="col align-start">
        <label v-if="showTimeLabel" class="black--text">Time</label>
        <div class="row relative">
          <input ref="button" v-model="selected" type="text" data-lpignore="true" :style="inputStyling" :disabled="!noedit" @keydown.enter="showPicker" @keydown.prevent.space="showPicker('space')" @keydown.esc="onBlur">
          <button v-if="noedit" tabindex="-1" type="button" class="absolute p-center-y p-right px-15 center element" :style="boxStyling">
            <s-icon height="16" :color="show ? 'white' : 'grey'">
              clock-outline
            </s-icon>
          </button>
        </div>
      </div>
    </div>

    <Teleport to="body" :disabled="!isMobile">
    <transition name="dropdown">
      <div v-show="show" ref="clockContainerEl" v-hotkey="{ esc: onBlur }" class="clockContainer">
        <FocusLoop>
          <div class="time">
            <div class="d-flex pl-40">
              <button class="opacity-40 pointer black--text" type="button" :class="{ selected: hours }" @click="hours = true">
                {{ time.format("hh") }}
              </button>
              <div>:</div>
              <button class="opacity-40 pointer black--text" type="button" :class="{ selected: !hours }" @click="hours = false">
                {{ time.format("mm") }}
              </button>
            </div>
            <div class="center d-col ml-10">
              <button class="font-18 opacity-40 pointer black--text" type="button" :class="{ selected: am }" @click="switchToAm()">
                AM
              </button>
              <button class="font-18 opacity-40 pointer black--text" type="button" :class="{ selected: !am }" @click="switchToPm()">
                PM
              </button>
            </div>
          </div>
          <div v-if="hours" class="center w100 h100">
            <div :class="['clock', 'center', 'noSelect']">
              <div :class="['hand', `hand-${pickerId}`]" :style="handStyle" />
              <div class="dot" />
              <button
                v-for="index in 12"
                :key="index"
                type="button"
                :class="[`time-${index}`, `time-${index}-${pickerId}`, { active: hours && hour12Num === index }]"
                @click="select($event.currentTarget)"
              >
                {{ index }}
              </button>
            </div>
          </div>
          <div v-if="!hours" class="center w100 h100">
            <div :class="['clock', 'center', 'noSelect']">
              <div :class="['hand', `hand-${pickerId}`]" :style="handStyle" />
              <div class="dot" />
              <button
                v-for="index in 12"
                :key="index"
                type="button"
                :class="[`time-${index}`, `time-${index}-${pickerId}`, { active: !hours && minuteSlotIndex === index }]"
                @click="select($event.currentTarget)"
              >
                {{ (index * 5) % 60 }}
              </button>
            </div>
          </div>
        </FocusLoop>
      </div>
    </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="picker-fade">
        <div v-show="show" class="picker-backdrop" @click="onBlur" />
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

const emit = defineEmits();
const props = defineProps({
  height: [String, Number],
  prefill: { type: null },
  noedit: { type: null },
  hideTimeLabel: Boolean
});

const showTimeLabel = computed(() => {
  const h = props.hideTimeLabel;
  if (h === true) return false;
  if (h === "" || h === "true") return false;
  return true;
});

const lang = computed(() => store.state.user.lang);

const pickerId = `timepicker-${Math.random().toString(36).substring(2, 11)}`;

const parsePrefill = (val) => {
  if (val === undefined || val === null || val === "") return dayjs();
  if (val && typeof val.isValid === "function" && val.isValid()) return val;
  const s = String(val).trim();
  const formats = ["hh:mm A", "h:mm A", "HH:mm", "H:mm"];
  for (const f of formats) {
    const d = dayjs(s, f, true);
    if (d.isValid()) return d;
  }
  const loose = dayjs(s, "hh:mm A");
  return loose.isValid() ? loose : dayjs();
};

let time = ref(parsePrefill(props.prefill));
let show = ref(false);
let hours = ref(true);
let am = ref(time.value.format("A") == "AM");
let selected = ref(time.value.locale(lang.value).format("hh:mm A"));
const button = ref(null);
const timePicker = ref(null);
const clockContainerEl = ref(null);
const isMobile = ref(window.innerWidth <= 576);
const onResizeMobile = () => { isMobile.value = window.innerWidth <= 576; };

const hour12Num = computed(() => ((time.value.hour() + 11) % 12) + 1);

const minuteSlotIndex = computed(() => {
  const m = time.value.minute();
  return m === 0 ? 12 : m / 5;
});

const handRotationDeg = computed(() => {
  if (hours.value) {
    return hour12Num.value * 30;
  }
  return minuteSlotIndex.value * 30;
});

const handStyle = computed(() => ({
  transform: `rotate(${handRotationDeg.value}deg) scaleY(1)`
}));

const inputStyling = computed(() => {
  let ob = {};
  if (props.height) ob.height = props.height + "px";
  if (!props.noedit) ob.backgroundColor = "#00000000";
  if (show.value) ob.backgroundColor = "var(--white)";
  return ob;
});

const boxStyling = computed(() => {
  let ob = {};
  if (props.height) ob.height = props.height + "px";
  if (props.height) ob.minWidth = props.height + "px";
  if (show.value) ob.backgroundColor = "var(--primary)";
  return ob;
});

const switchToAm = () => {
  let tempTime = time.value.subtract(12, "hour");
  if (!am.value) {
    time.value = tempTime;
    emit("changeTime", time.value);
  }
  am.value = true;
};

const switchToPm = () => {
  if (am.value) {
    time.value = time.value.add(12, "hour");
    emit("changeTime", time.value);
  }
  am.value = false;
};

const onFocus = () => {
  button.value?.focus();
};

const onBlur = () => {
  if (show.value) {
    emit('close');
  }
  show.value = false;
  checkInputTime();
};

const checkInputTime = () => {
  let normalizedTime = selected.value.trim().toUpperCase().split(" ").join("");
  if (normalizedTime.split(":")[0].length == 1) normalizedTime = `0${normalizedTime}`;
  let valid = dayjs(normalizedTime, "hh:mmA", true).isValid();
  selected.value = dayjs(normalizedTime, "hh:mmA").format("hh:mm A");
  if (!valid) selected.value = time.value.locale(lang.value).format("hh:mm A");
  button.value?.blur();
  let mod = modFive(selected.value);
  if (mod.charAt(0) == "0") return preSelectAtime(mod.substring(1));
  preSelectAtime(mod);
};

const modFive = (val) => {
  let tempTime = dayjs(val, "hh:mm A");
  if (!tempTime.isValid()) return val;
  const mins = Number(tempTime.format("mm"));
  const ceil = Math.min(Math.ceil(mins / 5) * 5, 55);
  tempTime = tempTime.minute(ceil);
  return tempTime.format("hh:mm A");
};

const listen = (event) => {
  if (!event || !timePicker.value) return;
  if (!timePicker.value.contains(event.target) && !clockContainerEl.value?.contains(event.target)) {
    onBlur();
  }
};

const showPicker = (key) => {
  if (!button.value) return;
  let index = button.value.selectionStart;
  if (key == "space") {
    const pair = Array.from(selected.value);
    pair.splice(index, 0, " ");
    selected.value = pair.join("");
    setTimeout(() => {
      setCaretPosition(index + 1);
    }, 0);
    return;
  }
  if (!props.noedit) return;
  if (!show.value) {
    show.value = true;
    emit('open');
    onFocus();
  } else {
    onBlur();
  }
};

const select = (el) => {
  if (!el) return;
  const base = time.value.isValid() ? time.value : dayjs();
  const n = Number.parseInt(String(el.textContent ?? el.innerHTML).trim(), 10);
  if (Number.isNaN(n)) return;
  let tempTime;
  if (hours.value) {
    const h24 = am.value ? (n === 12 ? 0 : n) : (n === 12 ? 12 : n + 12);
    tempTime = base.hour(h24).minute(base.minute()).second(0).millisecond(0);
  } else {
    tempTime = base.minute(n).second(0).millisecond(0);
  }
  if (!tempTime.isValid()) return;
  time.value = tempTime;
  emit("changeTime", tempTime);
};

const setCaretPosition = (position) => {
  let element = button.value;
  if (!element) return;
  element.focus();
  if (element.setSelectionRange) {
    element.setSelectionRange(position, position);
  } else if (element.createTextRange) {
    const range = element.createTextRange();
    range.collapse(true);
    range.moveEnd("character", position);
    range.moveStart("character", position);
    range.select();
  }
};

const preSelectAtime = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return;
  if (!timePicker.value) return;

  const parsed = dayjs(timeStr, "hh:mm A");
  if (!parsed.isValid()) return;

  const hour24 = parsed.hour();
  const minutesRaw = parsed.minute();

  const hour12 = ((hour24 + 11) % 12) + 1;
  const snapped = minutesRaw === 0 ? 0 : Math.min(Math.ceil(minutesRaw / 5) * 5, 55);
  const minuteIndex = snapped === 0 ? 12 : snapped / 5;

  const container = timePicker.value.querySelector('.clock');
  if (!container) return;

  const elHour = container.querySelector(`.time-${hour12}`);
  const elMinute = container.querySelector(`.time-${minuteIndex}`);
  if (!elHour || !elMinute) return;

  if (hours.value) {
    select(elHour);
  } else {
    select(elMinute);
  }
};

watch(time, (val) => {
  selected.value = dayjs(val).locale(lang.value).format("hh:mm A");
});

watch(
  () => props.prefill,
  (newPrefill) => {
    if (!newPrefill) return;
    const parsed = parsePrefill(newPrefill);
    if (!parsed.isValid()) return;
    if (parsed.format("HH:mm") === time.value.format("HH:mm")) return;
    time.value = parsed;
    selected.value = time.value.locale(lang.value).format("hh:mm A");
    am.value = time.value.format("A") === "AM";
  }
);

watch(lang, async () => {
  selected.value = time.value.locale(lang.value).format("hh:mm A");
});

onMounted(() => {
  if (!time.value.isValid()) time.value = dayjs();
  selected.value = time.value.locale(lang.value).format("hh:mm A");
  window.addEventListener("click", listen);
  window.addEventListener("resize", onResizeMobile);
});

onUnmounted(() => {
  window.removeEventListener("click", listen);
  window.removeEventListener("resize", onResizeMobile);
  if (show.value) {
    emit("close");
  }
});

defineExpose({ showPicker, close: onBlur });

</script>


<style scoped>
.clock>button {
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  cursor: pointer;
  color: var(--shade7);
  font-size: 14px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.clock>button:hover:not(.active) {
  background-color: color-mix(in srgb, var(--primary) 14%, var(--white));
  color: var(--primary);
}

.element {
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  border-radius: 8px;
}

.active {
  background-color: var(--primary);
  color: var(--shade1) !important;
}

.selected {
  opacity: 1;
}

.hand {
  height: calc(50% - 15px);
  width: 2px;
  bottom: 50%;
  left: calc(50% - 1px);
  transform-origin: center bottom;
  position: absolute;
  will-change: transform;
  z-index: 1;
  background-color: var(--primary);
}

.dot {
  position: absolute;
  bottom: 50%;
  transform: translate(0%, 50%);
  height: 5px;
  width: 5px;
  background-color: var(--primary);
  border-radius: 50%;
}

.time-12 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 50%;
  top: 0%;
}

.time-1 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 75%;
  top: 6.69873%;
  transform: translate(-50%, -50%);
}

.time-2 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 93.3013%;
  top: 25%;
}

.time-3 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 100%;
  top: 50%;
}

.time-4 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 93.3013%;
  top: 75%;
}

.time-5 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 75%;
  top: 93.3013%;
}

.time-6 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 50%;
  top: 100%;
}

.time-7 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 25%;
  top: 93.3013%;
}

.time-8 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 6.69873%;
  top: 75%;
}

.time-9 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 0%;
  top: 50%;
}

.time-10 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 6.69873%;
  top: 25%;
}

.time-11 {
  position: absolute;
  transform: translate(-50%, -50%);
  left: 25%;
  top: 6.69873%;
}

.clockContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 300px;
  height: 320px;
  box-shadow:
    0 18px 44px -16px rgba(16, 24, 39, 0.35),
    0 4px 12px -6px rgba(16, 24, 39, 0.12);
  color: var(--shade7);
  background-color: var(--white);
  border: 1px solid var(--shade4);
  position: absolute;
  z-index: 101;
  right: 0;
  border-radius: 16px;
  bottom: 56px;
  padding: 8px 0 16px;
  box-sizing: border-box;
}

.clock {
  background-color: var(--shade2);
  width: 180px;
  height: 180px;
  border-radius: 50%;
  position: relative;
  border: 25px solid var(--shade2);
  color: var(--shade7);
}


.time {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 44px;
  font-weight: 600;
  color: var(--shade5);
  width: 100%;
  height: 60px;
  position: relative;
  top: 10px;
  padding-bottom: 10px;
}

.time button {
  color: var(--shade5);
  transition: color 0.15s ease;
}

.time button.selected {
  color: var(--shade7);
}

.time .selected {
  opacity: 1;
}

input {
  background-color: var(--shade3);
  border: 1.5px solid var(--shade4);
  font-size: 14px;
  color: var(--black);
  padding: 12px 19px 12px 19px;
  width: 100%;
  box-sizing: border-box;
  caret-color: transparent;
  -webkit-appearance: none;
  border-radius: 0;
  cursor: pointer;
  user-select: none;
  pointer-events: auto;
  border-radius: 8px;
  transition: all 0.3s ease;
 height: 50px;
}

input:hover {
  background-color: var(--shade3);
}

input:focus {
  background-color: var(--shade3);
  color: var(--black);
  font-size: 14px;
  outline: none;
}

.line {
  width: 0;
  height: 1px;
  background-color: var(--primary);
  transition: all ease 0.2s;
}

input {
  background-color: var(--shade3);
  border: 1.5px solid var(--shade4);
  color: var(--black);
  padding: 12px 19px 12px 19px;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  /* caret-color: transparent; */
  user-select: none;
  border-radius: 8px;
}

input:hover {
  background-color: var(--shade3);
}

input:focus {
  background-color: var(--shade1);
  color: var(--black);
  font-size: 14px;
  outline: none;
}

input::selection {
  background: none;
}

input::selection {
  background: none;
}

button:focus {
  font-weight: 600;
}

input:disabled {
  cursor: default;
}

/* dimmed backdrop behind the picker on mobile (centered modal) */
.picker-backdrop {
  display: none;
}

.picker-fade-enter-active,
.picker-fade-leave-active {
  transition: opacity 0.2s ease;
}

.picker-fade-enter-from,
.picker-fade-leave-to {
  opacity: 0;
}

@media only screen and (max-width: 576px) {
  .clockContainer {
    position: fixed;
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    transform: translate(-50%, -50%);
    z-index: 1001;
  }

  .picker-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(16, 24, 39, 0.45);
    z-index: 1000;
  }
}
</style>